// src/pages/Profile.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ProfileService } from '@/services/profile.service';

export function ProfilePage() {
  const { user, profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: profile?.name || user?.displayName || '',
    company: profile?.company || '',
  });

  // Update form data when profile changes
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || user?.displayName || '',
        company: profile.company || '',
      });
    }
    setIsLoading(false);
  }, [profile, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await ProfileService.updateProfile(formData);
      await updateProfile(response.profile); // Update context with new profile data
      navigate('/');
    } catch (error) {
      console.error('Error saving profile:', error);
      // Handle API errors appropriately
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="sl-profile-page max-w-2xl">
      <h1 className="sl-page-title text-2xl font-bold text-gray-900">
        {profile ? 'Update Profile' : 'Complete Your Profile'}
      </h1>
      {/* <p className="sl-page-description mt-2 text-gray-600">
        {profile
          ? 'Update your profile information below.'
          : 'Please complete your profile information to continue.'}
      </p> */}

      <p className="sl-user-email mt-4 text-sm text-gray-600">
        You are logged in using your Google Account{' '}
        <span className="font-mono text-primary-400">{user?.email}</span>.{' '}
        {profile
          ? 'Update your profile details below to keep your information current.'
          : 'Please complete your profile details below to continue using the platform.'}
      </p>

      {isLoading ? (
        <div className="sl-loading mt-6 text-center">
          Loading profile data...
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="sl-profile-form mt-6 space-y-6">
          <div className="sl-form-group">
            <label
              htmlFor="name"
              className="sl-form-label block text-sm font-medium text-gray-700">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="sl-form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-400 focus:ring-primary-400"
            />
          </div>

          <div className="sl-form-group">
            <label
              htmlFor="company"
              className="sl-form-label block text-sm font-medium text-gray-700">
              Company <span className="text-xs text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="sl-form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-400 focus:ring-primary-400"
            />
          </div>

          <div className="sl-form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="sl-submit-button flex w-full justify-center rounded-md bg-primary-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 disabled:opacity-50">
              {isSubmitting
                ? 'Saving...'
                : profile
                  ? 'Update Profile'
                  : 'Complete Profile'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
